import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { ChatInputShell } from "@/components/ai/chat/ChatInputShell";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";
import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";

import { AgentModeDropdown } from "@/components/ai/prompt-input/AgentModeDropdown";
import { AgentMode, applyAgentModePrefix, getAgentModePlaceholder } from "@/components/ai/prompt-input/agent-mode";
import { AddImageButton, type ImageAttachment } from "@/components/ai/prompt-input/ImageUpload";
import { ImageAttachmentStrip } from "@/components/ai/prompt-input/ImageAttachmentStrip";
import { useImageDrop, useImagePaste } from "@/components/ai/prompt-input/use-image-drop";
import { SupportBundleUnavailableModal } from "@/components/ai/SupportBundleUnavailableModal";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import type { CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useExperimentEnabled } from "@/hooks/use-experiment";
import { useFlagAgentImageAttachmentsEnabled, useFlagOnaAIEnabled } from "@/hooks/use-flag";

import { useMembership } from "@/hooks/use-membership";
import { usePersistedInput } from "@/hooks/use-persisted-input";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { useStopService } from "@/queries/automations-queries";
import { extractPromptValidationDetails, formatError } from "@/utils/errors";
import type { PromptValidationWarningWithOriginal } from "@/components/ai/prompt-input/state";
import { PromptValidationWarningBanner } from "@/components/ai/prompt-input/PromptValidationWarningBanner";
import { isTouchDevice } from "@/utils/is-touch-device";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_ImageInput,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useAtom } from "jotai";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { ChatInputPopover } from "@/components/ai/chat/ChatInputPopover";
import { MCPIntegrationsList } from "@/components/ai/chat/MCPIntegrationsList";
import { MCPIntegrationsButton } from "@/components/ai/chat/MCPIntegrationsButton";
import { useListMCPIntegrations, type MCPErrorSnapshot } from "@/queries/integration-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import type { AvailableCommand } from "@/hooks/use-stream-conversation";

export type NewPendingMessage = {
    id: string;
    // If content is undefined, it means the message with `id` failed to be sent
    // and should be retracted again.
    content?: string;
};

export type ChatInputActiveState = {
    isActive: boolean;
    mode: AgentMode;
};

export type ChatInputProps = {
    agentExecution: PlainAgentExecution;
    environmentId?: string;
    placeholder?: string;
    onNewMessage: (message: NewPendingMessage) => void;
    hasMessages: boolean;
    autoFocus?: React.TextareaHTMLAttributes<HTMLTextAreaElement>["autoFocus"];
    onActiveStateChange?: (state: ChatInputActiveState) => void;
    disableModeSwitching?: boolean;
    availableCommands?: AvailableCommand[];
} & PropsWithClassName;

export const ChatInput: FC<ChatInputProps> = ({
    agentExecution,
    environmentId,
    onNewMessage,
    className,
    placeholder,
    autoFocus,
    onActiveStateChange,
    disableModeSwitching,
    availableCommands,
}) => {
    const { value: isOnaAgentCommandsEnabled } = useFlagOnaAIEnabled();
    const isRalphModeEnabled = useExperimentEnabled("ralph");
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    const sendToAgentExecution = useSendToAgentExecution();
    const { handleStopAgentExecution, downloadSupportBundle, isSupportBundleAvailable } =
        useAgentExecutionActions(agentExecution);
    const { toast } = useToast();

    const storageKey = `agent-execution-input-${agentExecution.id}`;
    const [inputValue, setInputValue] = usePersistedInput(storageKey, "");
    const [showDeleteAgentExecutionModal, setShowDeleteAgentExecutionModal] = useState(false);
    const [showSupportBundleUnavailableModal, setShowSupportBundleUnavailableModal] = useState(false);
    const [agentMode, setAgentMode] = useAtom(agentModeAtomFamily(environmentId ?? ""));
    const [selectedImages, setSelectedImages] = useState<ImageAttachment[]>([]);
    const [promptValidationWarning, setPromptValidationWarning] = useState<PromptValidationWarningWithOriginal | null>(null);
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isActive = isFocused || isHovered;
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleFocusIn = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleFocusOut = useCallback((e: React.FocusEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
            setIsFocused(false);
        }
    }, []);

    // Notify parent of active state and mode changes
    useEffect(() => {
        onActiveStateChange?.({ isActive, mode: agentMode });
    }, [isActive, agentMode, onActiveStateChange]);

    // Refocus textarea after mode change so the user can keep typing
    const prevModeRef = useRef(agentMode);
    useEffect(() => {
        if (prevModeRef.current !== agentMode) {
            prevModeRef.current = agentMode;
            requestAnimationFrame(() => textareaRef.current?.focus());
        }
    }, [agentMode]);

    const organizationId = membership?.organizationId;
    const stopService = useStopService();

    // Detect MCP integration auth token changes (connect or disconnect) and restart the agent
    const { data: mcpIntegrations, isLoading: mcpLoading } = useListMCPIntegrations(organizationId);
    const { data: authTokensData, isLoading: tokensLoading } = useListHostAuthenticationTokens({ enabled: true });
    const dataReady = !mcpLoading && !tokensLoading;
    const mcpIntegrationIds = useMemo(() => new Set(mcpIntegrations?.map((i) => i.id)), [mcpIntegrations]);
    const authTokenIntegrationIds = useMemo(() => {
        if (!authTokensData) return new Set<string>();
        return new Set(
            authTokensData.tokens
                .map((t) => t.integrationId)
                .filter((id): id is string => !!id && mcpIntegrationIds.has(id)),
        );
    }, [authTokensData, mcpIntegrationIds]);

    const mcpErrorSnapshotsRef = useRef<Map<string, MCPErrorSnapshot>>(new Map());

    const prevAuthTokenIds = useRef<Set<string> | null>(null);
    useEffect(() => {
        if (!dataReady) return;

        const prev = prevAuthTokenIds.current;
        prevAuthTokenIds.current = authTokenIntegrationIds;

        if (prev === null) return;

        const added = [...authTokenIntegrationIds].some((id) => !prev.has(id));
        const removed = [...prev].some((id) => !authTokenIntegrationIds.has(id));

        if ((added || removed) && environmentId) {
            void stopService.mutateAsync({
                reference: `agent-${agentExecution.spec?.agentId}`,
                environmentId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authTokenIntegrationIds, dataReady, environmentId, agentExecution.spec]);

    const handleImagesSelected = useCallback((images: ImageAttachment[]) => {
        setSelectedImages((prev) => [...prev, ...images]);
    }, []);

    const handleRemoveImage = useCallback((id: string) => {
        setSelectedImages((prev) => {
            const image = prev.find((img) => img.id === id);
            if (image) {
                URL.revokeObjectURL(image.preview);
            }
            return prev.filter((img) => img.id !== id);
        });
    }, []);

    const {
        isDragging,
        isProcessing: isDropProcessing,
        dragHandlers,
    } = useImageDrop(handleImagesSelected, !imageAttachmentsEnabled);
    const { handlePaste, isProcessing: isPasteProcessing } = useImagePaste(
        handleImagesSelected,
        !imageAttachmentsEnabled,
    );
    const isImageProcessing = isDropProcessing || isPasteProcessing;

    // Build available agent modes based on feature flags
    const availableAgentModes = useMemo(() => {
        const modes: AgentMode[] = [AgentMode.Agent, AgentMode.Spec];
        if (isRalphModeEnabled) {
            modes.push(AgentMode.Ralph);
        }
        return modes;
    }, [isRalphModeEnabled]);

    const showAgentModeDropdown = availableAgentModes.length > 1;

    const onSubmit = useCallback(
        async (text: string, messageId: string, images?: ImageAttachment[]) => {
            if (!agentExecution.id) {
                return;
            }

            // Build inputs array with text and images
            const inputs: UserInputBlock_Input[] = [];

            // Only include text input if there's actual content
            if (text) {
                inputs.push(
                    new UserInputBlock_Input({
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: text,
                            }),
                        },
                    }),
                );
            }

            // Add images to inputs array
            for (const image of images ?? []) {
                inputs.push(
                    new UserInputBlock_Input({
                        input: {
                            case: "image",
                            value: new UserInputBlock_ImageInput({
                                data: image.data,
                                mimeType: image.mimeType,
                            }),
                        },
                    }),
                );
            }

            // Send single message with all inputs
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    input: {
                        case: "userInput" as const,
                        value: new UserInputBlock({
                            id: messageId,
                            inputs,
                        }),
                    },
                }),
            );
        },
        [agentExecution.id, sendToAgentExecution],
    );

    const handleSendMessage = useCallback(
        async (value: string) => {
            const trimmedValue = value.trim();
            const hasImages = imageAttachmentsEnabled && selectedImages.length > 0;
            if (!trimmedValue && !hasImages) {
                return;
            }

            // Handle built-in commands (only if feature flag is enabled)
            if (isOnaAgentCommandsEnabled) {
                if (trimmedValue === "/clear") {
                    setInputValue("");
                    setShowDeleteAgentExecutionModal(true);
                    return;
                }

                if ((trimmedValue === "/skills" || trimmedValue === "/commands") && isOrgAdmin) {
                    setInputValue("");
                    window.open("/settings/agents", "_blank");
                    return;
                }
                if (trimmedValue === "/support-bundle") {
                    setInputValue("");
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return;
                }
            }

            if (sendToAgentExecution.isPending) {
                return;
            }

            if (isImageProcessing) {
                toast({
                    title: "Please wait",
                    description: "Image is still being processed",
                });
                return;
            }

            const messageToSend = trimmedValue ? applyAgentModePrefix(trimmedValue, agentMode) : "";

            const id = uuidv4();
            const imagesToSend = imageAttachmentsEnabled ? [...selectedImages] : undefined;
            const pendingContent = trimmedValue;
            try {
                setInputValue("");
                // Clear images before sending (optimistic)
                if (imagesToSend && imagesToSend.length > 0) {
                    setSelectedImages([]);
                }
                onNewMessage({ id, content: pendingContent });
                await onSubmit(messageToSend, id, imagesToSend);
                // Revoke blob URLs after successful send
                for (const image of imagesToSend ?? []) {
                    URL.revokeObjectURL(image.preview);
                }
            } catch (error) {
                setInputValue(value);
                // Restore images on failure
                if (imagesToSend && imagesToSend.length > 0) {
                    setSelectedImages(imagesToSend);
                }
                onNewMessage({ id, content: undefined });

                const validationDetails = extractPromptValidationDetails(error);
                if (validationDetails) {
                    setInputValue(validationDetails.cleanedText);
                    setPromptValidationWarning({ ...validationDetails, originalText: value });
                } else {
                    toast({
                        title: "Error sending to session",
                        description: formatError(error),
                    });
                }
            }
        },
        [
            sendToAgentExecution.isPending,
            setInputValue,
            onNewMessage,
            onSubmit,
            toast,
            isOnaAgentCommandsEnabled,
            isOrgAdmin,
            downloadSupportBundle,
            isSupportBundleAvailable,
            setShowSupportBundleUnavailableModal,
            agentMode,
            imageAttachmentsEnabled,
            selectedImages,
            isImageProcessing,
            setPromptValidationWarning,
        ],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                await handleStopAgentExecution();
                return;
            }
            // On touch devices, let Enter insert newlines (users submit via Send button)
            // On desktop, Enter submits and Shift+Enter inserts newlines
            if (e.key === "Enter" && !e.shiftKey && !isTouchDevice()) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                await handleSendMessage(input.value);
                return;
            }
        },
        [handleStopAgentExecution, handleSendMessage],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
        setPromptValidationWarning(null);
    };

    const handleSuggestionSelect = useCallback(
        (suggestion: CommandSuggestion) => {
            // Check if this is a builtin command
            if (suggestion.id.startsWith("builtin-")) {
                // Execute builtin commands directly
                if (suggestion.value === "/clear") {
                    setShowDeleteAgentExecutionModal(true);
                    return true; // Prevent default pasting behavior
                }

                if ((suggestion.value === "/skills" || suggestion.value === "/commands") && isOrgAdmin) {
                    window.open("/settings/agents", "_blank");
                    return true; // Prevent default pasting behavior
                }

                if (suggestion.value === "/support-bundle") {
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return true; // Prevent default pasting behavior
                }
            }
            // For non-builtin commands, allow the default pasting behavior
            return false;
        },
        [isOrgAdmin, downloadSupportBundle, isSupportBundleAvailable, setShowSupportBundleUnavailableModal],
    );

    const isInputEmpty = !inputValue.trim() && selectedImages.length === 0;

    return (
        <>
            {promptValidationWarning && (
                <PromptValidationWarningBanner
                    warning={promptValidationWarning}
                    onUndo={() => {
                        setInputValue(promptValidationWarning.originalText);
                        setPromptValidationWarning(null);
                    }}
                />
            )}
            <ChatInputShell
                ref={containerRef}
                isDragging={isDragging}
                dragHandlers={dragHandlers}
                agentMode={agentMode}
                isActive={isActive}
                onFocus={handleFocusIn}
                onBlur={handleFocusOut}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <ChatInputWithPrompts
                    ref={textareaRef}
                    agentExecution={agentExecution}
                    minHeight={40}
                    maxHeight={300}
                    popoverSide="top"
                    popoverSideOffset={20} // top padding + border + some spacing
                    popoverWidthOffset={32} // account for the total horizontal padding (px-4)
                    popoverAlignOffset={-16} // account for the left padding
                    placeholder={placeholder ?? getAgentModePlaceholder(agentMode)}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    onSuggestionSelect={handleSuggestionSelect}
                    value={inputValue}
                    className={cn("placeholder:text-content-inactive", className)}
                    data-testid="agent-execution-chat-input"
                    autoFocus={autoFocus}
                    enterKeyHint="enter"
                    availableCommands={availableCommands}
                    disabledMessage={
                        agentMode === AgentMode.Ralph
                            ? "Commands are not available in Ralph mode. Switch to Agent mode to use commands."
                            : undefined
                    }
                />
                {imageAttachmentsEnabled && selectedImages.length > 0 && (
                    <ImageAttachmentStrip images={selectedImages} onRemove={handleRemoveImage} />
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* Left side: Add image button and tools button */}
                    {/* negative margin to align icon with prompt text. 7.5px looks the most visually aligned */}
                    <div className="-ml-[7.5px] flex min-w-0 flex-1 flex-wrap items-center gap-1">
                        {imageAttachmentsEnabled && <AddImageButton onImagesSelected={handleImagesSelected} />}
                        {isImageProcessing && (
                            <span className="text-sm text-content-secondary">Processing image...</span>
                        )}
                        <ChatInputPopover
                            trigger={
                                <MCPIntegrationsButton
                                    agentExecution={agentExecution}
                                    organizationId={organizationId}
                                    errorSnapshots={mcpErrorSnapshotsRef.current}
                                    data-tracking-id="default-tracking-id"
                                />
                            }
                            content={
                                <MCPIntegrationsList
                                    organizationId={organizationId}
                                    agentExecution={agentExecution}
                                    errorSnapshots={mcpErrorSnapshotsRef.current}
                                />
                            }
                            title="MCP Integrations"
                            className="w-96"
                        />
                    </div>
                    {/* Right side: agent mode dropdown, send button */}
                    <div className="flex min-w-0 items-center gap-2">
                        {showAgentModeDropdown && (
                            <div className="-mr-1">
                                <AgentModeDropdown
                                    mode={agentMode}
                                    onModeChange={setAgentMode}
                                    availableModes={availableAgentModes}
                                    disabled={disableModeSwitching}
                                />
                            </div>
                        )}

                        {/* Send Button */}
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    Send
                                    <div className="flex items-center gap-1">
                                        <KeyboardKey>Enter</KeyboardKey>
                                    </div>
                                </div>
                            }
                        >
                            <Button
                                id="send-button"
                                className="-mr-2 size-8 p-0"
                                LeadingIcon={IconArrowUp}
                                aria-label="Send message"
                                onClick={() => handleSendMessage(inputValue)}
                                disabled={isInputEmpty || sendToAgentExecution.isPending || isImageProcessing}
                                data-tracking-id="send-to-agent-execution-chat-input"
                            />
                        </Tooltip>
                    </div>
                </div>
            </ChatInputShell>

            <DeleteAgentExecutionModal
                agentExecution={agentExecution}
                open={showDeleteAgentExecutionModal}
                onClose={() => setShowDeleteAgentExecutionModal(false)}
                inputValue={inputValue}
            />

            <SupportBundleUnavailableModal
                open={showSupportBundleUnavailableModal}
                onClose={() => setShowSupportBundleUnavailableModal(false)}
                agentExecution={agentExecution}
            />
        </>
    );
};
